import {
  INSIGHT_EXPLANATIONS,
  type InsightMetric,
  type Insights as InsightsT,
  type Metric,
} from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { api, patch, post } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

const num = (v: number) => v.toLocaleString("en-GB");
const dayLabel = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });

function Change({ m }: { m: Metric }) {
  if (m.change === null) return <span className="muted small">new</span>;
  const up = m.change >= 0;
  return (
    <span className={`change ${up ? "up" : "down"}`}>
      {up ? "+" : ""}
      {m.change}%
    </span>
  );
}

function Tile({
  label,
  metric,
  value,
  explain,
}: {
  label: string;
  metric: Metric;
  value: string;
  explain: string;
}) {
  return (
    <div className="card tile">
      <div className="muted small">{label}</div>
      <div className="row" style={{ alignItems: "baseline", gap: 10 }}>
        <span className="stat">{value}</span>
        <Change m={metric} />
      </div>
      <p className="explain">{explain}</p>
    </div>
  );
}

/** One measure per chart (no second axis): bars per day with a hover readout. */
function Bars({
  title,
  series,
  pick,
  color,
  format,
}: {
  title: string;
  series: InsightsT["series"];
  pick: (d: InsightsT["series"][number]) => number;
  color: string;
  format: (v: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 640;
  const H = 120;
  const max = Math.max(1, ...series.map(pick));
  const step = W / series.length;
  const bar = Math.max(2, step - 2);
  const shown = hover ?? series.length - 1;
  const d = series[shown];
  return (
    <figure className="chart">
      <figcaption className="row">
        <b className="spacer">{title}</b>
        {d && (
          <span className="muted small">
            {dayLabel(d.day)}: <b style={{ color: "var(--ink)" }}>{format(pick(d))}</b>
          </span>
        )}
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H + 18}`}
        role="img"
        aria-label={`${title} per day, highest ${format(max)}`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const i = Math.floor(((e.clientX - box.left) / box.width) * series.length);
          setHover(Math.max(0, Math.min(series.length - 1, i)));
        }}
      >
        <line x1="0" x2={W} y1={H} y2={H} stroke="var(--line)" />
        {series.map((s, i) => {
          const h = Math.max(pick(s) > 0 ? 2 : 0, (pick(s) / max) * (H - 8));
          return (
            <g key={s.day}>
              <rect
                x={i * step + 1}
                y={H - h}
                width={bar}
                height={h}
                rx={Math.min(4, bar / 2)}
                fill={color}
                opacity={hover === null || hover === i ? 1 : 0.45}
              />
            </g>
          );
        })}
        <text x="0" y={H + 14} className="axis">
          {dayLabel(series[0]!.day)}
        </text>
        <text x={W} y={H + 14} textAnchor="end" className="axis">
          {dayLabel(series[series.length - 1]!.day)}
        </text>
      </svg>
      <details className="small">
        <summary className="muted">Show as a table</summary>
        <table className="table">
          <tbody>
            {series.map((s) => (
              <tr key={s.day}>
                <td>{dayLabel(s.day)}</td>
                <td>{format(pick(s))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

export function Insights() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const qc = useQueryClient();
  const toast = useToast();
  const [days, setDays] = useState(30);
  const [wall, setWall] = useState("");
  const q = useQuery({
    queryKey: ["insights", ws, days, wall],
    queryFn: () =>
      api<InsightsT>(`/v1/w/${ws}/insights?days=${days}${wall ? `&wall=${wall}` : ""}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["insights", ws] });
  const d = q.data;
  if (!d) return <p className="muted">Loading…</p>;
  const t = d.totals;
  const f = d.freshness;
  const tile = (k: InsightMetric, label: string, value: string) => (
    <Tile label={label} metric={t[k]} value={value} explain={INSIGHT_EXPLANATIONS[k]} />
  );
  const empty = t.views.value === 0 && t.views.previous === 0;

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row wrap">
        <p className="spacer muted">{d.summary}</p>
        <select
          className="input"
          style={{ width: "auto" }}
          value={wall}
          onChange={(e) => setWall(e.target.value)}
          aria-label="Wall"
        >
          <option value="">All walls</option>
          {d.walls.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
              {w.status === "live" ? "" : ` (${w.status})`}
            </option>
          ))}
        </select>
        <select
          className="input"
          style={{ width: "auto" }}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="Period"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {empty && (
        <div className="card">
          <b>No visits counted yet.</b>{" "}
          <span className="muted">
            Numbers show up here as soon as shoppers see your wall on your site, within seconds.
          </span>
        </div>
      )}

      <div className="tiles">
        {tile("views", "Wall views", num(t.views.value))}
        {tile("opens", "Posts opened", num(t.opens.value))}
        {tile("clicks", "Product clicks", num(t.clicks.value))}
        {tile("adds", "Added to bag", num(t.adds.value))}
      </div>

      <div className="wide-left">
        <section className="card stack">
          <Bars
            title="Wall views"
            series={d.series}
            pick={(s) => s.views}
            color="var(--ink)"
            format={num}
          />
          <Bars
            title="Product clicks"
            series={d.series}
            pick={(s) => s.clicks}
            color="var(--berry)"
            format={num}
          />
        </section>
        <section className="card stack">
          <h2>Freshness</h2>
          <div className="row" style={{ alignItems: "baseline", gap: 10 }}>
            <span className="stat">{f.score}</span>
            <span className="muted">of 100</span>
          </div>
          <div className="meter" aria-hidden="true">
            <i style={{ width: `${f.score}%` }} />
          </div>
          <div className="row">
            <div className="spacer">
              <div className="muted small">Newest post</div>
              <b>
                {f.newestAgeDays === null
                  ? "None live"
                  : `${f.newestAgeDays} day${f.newestAgeDays === 1 ? "" : "s"}`}
              </b>
            </div>
            <div className="spacer">
              <div className="muted small">Average age</div>
              <b>{f.averageAgeDays === null ? "None live" : `${f.averageAgeDays} days`}</b>
            </div>
          </div>
          {f.tip && <p className="small">{f.tip}</p>}
          <p className="explain">{INSIGHT_EXPLANATIONS.freshness}</p>
        </section>
      </div>

      <div className="grid2">
        <section className="card stack">
          <h2>Top posts</h2>
          {d.topPosts.length === 0 && (
            <p className="muted small">No post has been opened or clicked yet.</p>
          )}
          {d.topPosts.map((p) => (
            <div className="row" key={p.id}>
              <div className="thumb-sm">{p.previewUrl && <img src={p.previewUrl} alt="" />}</div>
              <div className="spacer">
                <b>@{p.creator}</b>
                <div className="muted small">
                  {num(p.clicks)} clicks · opened {num(p.opens)} times
                  {p.adds ? ` · ${num(p.adds)} added to bag` : ""}
                </div>
              </div>
            </div>
          ))}
        </section>
        <section className="card stack">
          <h2>Top creators</h2>
          {d.topCreators.length === 0 && (
            <p className="muted small">No post has been opened or clicked yet.</p>
          )}
          {d.topCreators.map((c) => (
            <div className="row" key={c.username}>
              <div className="avatar">{c.username.slice(0, 2).toUpperCase()}</div>
              <div className="spacer">
                <b>@{c.username}</b>
                <div className="muted small">
                  {c.posts} post{c.posts === 1 ? "" : "s"} · {num(c.clicks)} clicks · opened{" "}
                  {num(c.opens)} times
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>

      <section className="card row wrap">
        <div className="spacer">
          <h2>Monthly report</h2>
          <p className="muted small">
            A short email on the 1st of each month to owners and approvers: the numbers, the top
            posts, and what's waiting for you.
          </p>
        </div>
        {workspace.role !== "viewer" && (
          <button
            type="button"
            className="btn secondary small"
            onClick={async () => {
              const r = await post<{ summary: string }>(`/v1/w/${ws}/emails/monthly`);
              toast(r.summary);
            }}
          >
            Send it to me now
          </button>
        )}
        {workspace.role === "owner" && (
          <label className="switch" title="Monthly report">
            <input
              type="checkbox"
              checked={d.monthlyEmail}
              aria-label="Send the monthly report"
              onChange={async (e) => {
                await patch(`/v1/w/${ws}/insights/settings`, { monthlyEmail: e.target.checked });
                refresh();
              }}
            />
            <span />
          </label>
        )}
      </section>
    </div>
  );
}
